import {MetaDataEntityDto} from '../../../../meta/dto/meta-data-entity.dto';
import {MetaDataAttributeKind} from '../../../../meta/logic/meta-data-attribute-kind';
import {QlBuilderGroupByAggregateFunction, QlBuilderModel, QlBuilderSideCarNode} from '../../../builder/ql-builder-model';
import {QlBuilderContainerNode} from './ql-builder-tree-ops';

/**
 * German display labels for the result keys of a QlBuilderModel (`<alias>.<name|as>`, e.g. `e.status`,
 * `j1.name`, `e.count`) — for dropdowns that otherwise show raw QL keys (sorting, widget mapping).
 *
 * Labels come from the entity metadata where it is loaded (attribute / join `label`) and from the model
 * itself (aggregate function, date-group precision). A key the metadata does not know keeps the key as
 * its label, so a label is never empty and never wrong — only less friendly. Callers show the key as
 * secondary text next to the label.
 *
 * Pure (no Angular): the component loads the metadata (`MetaService.getByAlias`) for the aliases
 * {@link entityAliases} returns and hands the loaded entries back in.
 */
export class QlBuilderKeyLabels {

    static readonly FUNCTION_LABELS: Readonly<Record<QlBuilderGroupByAggregateFunction, string>> = {
        count: 'Anzahl',
        sum: 'Summe',
        avg: 'Durchschnitt',
        min: 'Minimum',
        max: 'Maximum',
    };

    static readonly PRECISION_LABELS: Readonly<Record<string, string>> = {
        day: 'je Tag',
        month: 'je Monat',
        year: 'je Jahr',
    };

    /**
     * Entity aliases of every regular node of the tree that can be resolved with the metadata loaded so
     * far — the root always, a join once its parent's metadata is known. Load the missing ones and call
     * again: each round resolves one more tree level.
     */
    static entityAliases(model: QlBuilderModel | null, metas: ReadonlyMap<string, MetaDataEntityDto>): string[] {

        const aliases = new Set<string>();
        if (!model?.root?.entityName) {
            return [];
        }

        aliases.add(model.root.entityName);
        QlBuilderKeyLabels.walk(model.root, model.root.entityName, metas, (_node, entityAlias) => {
            if (entityAlias) {
                aliases.add(entityAlias);
            }
        });

        return Array.from(aliases);
    }

    /**
     * Per regular node alias (root and every join): the entity alias its fields belong to (`null` while the
     * parent's metadata is not loaded, or for a reverse join without a resolvable target) and the German
     * label of the node (the root's entity label, a join's meta label). The prefix a condition summary
     * uses ("Typ: Name ≠ …") is the join label; the root gets none.
     */
    static nodeInfo(model: QlBuilderModel | null,
                    metas: ReadonlyMap<string, MetaDataEntityDto>): Map<string, { entityAlias: string | null; label: string | null }> {

        const info = new Map<string, { entityAlias: string | null; label: string | null }>();
        const root = model?.root;
        if (!root?.entityName) {
            return info;
        }
        info.set(root.alias, {entityAlias: root.entityName, label: null});
        QlBuilderKeyLabels.walk(root, root.entityName, metas, (node, entityAlias, joinLabel) => {
            info.set(node.alias, {entityAlias, label: joinLabel});
        });
        return info;
    }

    /** Cache key of a side-car version for {@link labels}' `sideCarFieldLabels`: `<instanceAlias>|<scopeId>`. */
    static sideCarKey(instanceAlias: string, scopeId: number): string {
        return `${instanceAlias}|${scopeId}`;
    }

    /**
     * Side-car nodes of the tree whose field labels are neither in the model (`fieldLabels`) nor in
     * `sideCarFieldLabels` - the ones a component still has to ask the discovery for.
     */
    static sideCarsWithoutLabels(model: QlBuilderModel | null,
                                 sideCarFieldLabels: ReadonlyMap<string, ReadonlyMap<number, string>>): QlBuilderSideCarNode[] {
        const missing: QlBuilderSideCarNode[] = [];
        const walk = (node: QlBuilderContainerNode): void => {
            for (const child of node.children ?? []) {
                if (child.kind === 'join') {
                    walk(child);
                } else if (child.kind === 'sideCar'
                    && !sideCarFieldLabels.has(QlBuilderKeyLabels.sideCarKey(child.instanceAlias, child.scopeId))
                    && child.typeFieldIds.some(id => !child.fieldLabels?.[id])) {
                    missing.push(child);
                }
            }
        };
        if (model?.root) {
            walk(model.root);
        }
        return missing;
    }

    /**
     * Label per result key of the model; keys without a known label map to themselves.
     *
     * @param sideCarFieldLabels TypeField labels per side-car version ({@link sideCarKey}) loaded from the
     *                           discovery - consulted for side-car nodes that carry no `fieldLabels` of their own
     */
    static labels(model: QlBuilderModel | null,
                  metas: ReadonlyMap<string, MetaDataEntityDto>,
                  sideCarFieldLabels: ReadonlyMap<string, ReadonlyMap<number, string>> = new Map()): Map<string, string> {

        const labels = new Map<string, string>();
        const root = model?.root;
        if (!root) {
            return labels;
        }

        const rootMeta = metas.get(root.entityName) ?? null;

        if (model.groupBy) {
            for (const field of model.groupBy.fields) {
                const base = QlBuilderKeyLabels.memberLabel(rootMeta, field.name);
                const precision = field.dateGroup ? QlBuilderKeyLabels.PRECISION_LABELS[field.dateGroup.precision] : null;
                labels.set(`${root.alias}.${field.as || field.name}`, precision ? `${base} (${precision})` : base);
            }
            for (const aggregate of model.groupBy.aggregates) {
                const fn = QlBuilderKeyLabels.FUNCTION_LABELS[aggregate.fn] ?? aggregate.fn;
                const label = aggregate.name === '*' ? fn : `${fn} ${QlBuilderKeyLabels.memberLabel(rootMeta, aggregate.name)}`;
                labels.set(`${root.alias}.${aggregate.as}`, label);
            }
        } else {
            for (const field of root.fields) {
                labels.set(`${root.alias}.${field.as || field.name}`, QlBuilderKeyLabels.memberLabel(rootMeta, field.name));
            }
        }

        QlBuilderKeyLabels.addSideCarLabels(root, labels, sideCarFieldLabels);
        QlBuilderKeyLabels.walk(root, root.entityName, metas, (node, entityAlias, joinLabel) => {
            const meta = entityAlias ? metas.get(entityAlias) ?? null : null;
            for (const field of node.fields ?? []) {
                const fieldLabel = QlBuilderKeyLabels.memberLabel(meta, field.name);
                labels.set(`${node.alias}.${field.as || field.name}`, joinLabel ? `${joinLabel}: ${fieldLabel}` : fieldLabel);
            }
            QlBuilderKeyLabels.addSideCarLabels(node, labels, sideCarFieldLabels);
        });

        return labels;
    }

    /** The label of one key, falling back to the key itself. */
    static labelOf(labels: ReadonlyMap<string, string>, key: string): string {
        return labels.get(key) || key;
    }

    /**
     * German label of an attribute, a scalar generic field (`g_<id>:value`) or an n:1 relationship name
     * of one entity; the raw name when the metadata does not know it.
     */
    static memberLabel(meta: MetaDataEntityDto | null, name: string): string {

        if (!meta || !name) {
            return name;
        }

        const generic = /^g_(\d+):/.exec(name);
        if (generic) {
            const id = Number(generic[1]);
            const attribute = (meta.attributes ?? []).find(a =>
                MetaDataAttributeKind.isGenericAttribute(a) && MetaDataAttributeKind.genericAttributeIdOf(a) === id);
            return attribute?.label || name;
        }

        const attribute = (meta.attributes ?? []).find(a => a.attributeName === name);
        if (attribute?.label) {
            return attribute.label;
        }

        const join = (meta.joins ?? []).find(j => j.name === name);
        return join?.label || name;
    }

    /** German label of one side-car field: the model's label, then the loaded discovery label, then "Zusatzfeld <id>". */
    static sideCarFieldLabel(child: QlBuilderSideCarNode,
                             typeFieldId: number,
                             sideCarFieldLabels: ReadonlyMap<string, ReadonlyMap<number, string>>): string {
        return child.fieldLabels?.[typeFieldId]
            || sideCarFieldLabels.get(QlBuilderKeyLabels.sideCarKey(child.instanceAlias, child.scopeId))?.get(typeFieldId)
            || `Zusatzfeld ${typeFieldId}`;
    }

    /**
     * Side-car columns are keyed on the HOST alias (`<hostAs>.ext_<id>`). The label is the TypeField name from the
     * model (`fieldLabels`, set when the side-car was applied) or from the discovery labels handed in; only a
     * side-car nobody has resolved yet names the kind and the id.
     */
    private static addSideCarLabels(node: QlBuilderContainerNode,
                                    labels: Map<string, string>,
                                    sideCarFieldLabels: ReadonlyMap<string, ReadonlyMap<number, string>>): void {
        for (const child of node.children ?? []) {
            if (child.kind === 'sideCar') {
                child.typeFieldIds.forEach(id =>
                    labels.set(`${node.alias}.ext_${id}`, QlBuilderKeyLabels.sideCarFieldLabel(child, id, sideCarFieldLabels)));
            }
        }
    }

    /** Visit every regular join node with its resolved entity alias (or `null`) and its German label. */
    private static walk(
        node: QlBuilderContainerNode,
        entityAlias: string,
        metas: ReadonlyMap<string, MetaDataEntityDto>,
        visit: (node: QlBuilderContainerNode, entityAlias: string | null, joinLabel: string | null) => void): void {

        const parentMeta = metas.get(entityAlias) ?? null;

        for (const child of node.children ?? []) {
            if (child.kind !== 'join') {
                continue;
            }
            const metaJoin = (parentMeta?.joins ?? []).find(j => j.name === child.name) ?? null;
            const childEntity = metaJoin?.joinObjectTypeAlias
                || (child.name.includes('#') ? child.name.split('#')[0] : null);
            const joinLabel = metaJoin?.label || child.name;

            visit(child, childEntity, joinLabel);

            if (childEntity) {
                QlBuilderKeyLabels.walk(child, childEntity, metas, visit);
            }
        }
    }
}
